/**
 * Inline-editable due-date cell for the Tasks Table.
 *
 * PR #38 replaced the native `<input type="date">` in tasks.table.tsx
 * with this Radix Popover-based picker so the table no longer pops the
 * macOS native widget (the native one renders white-on-white against
 * the dark shell and lacks Australian formatting). The picker itself
 * is a custom 6-row × 7-col grid built with `date-fns`; we deliberately
 * avoid a heavier calendar dependency because the surface area is so
 * small (six rows, one month at a time, navigate by chevron).
 *
 * Behaviour summary:
 *   - trigger button shows `task.dueDate` (formatted DD MMM YY) or `—`
 *     when null; styled to fill the cell like the sibling inline editors
 *   - clicking the trigger opens a Radix Popover anchored to the cell
 *   - the calendar grid renders six weeks starting on Monday (Australian
 *     convention); days outside the focused month are dimmed
 *   - chevrons navigate month-by-month; the year follows
 *   - clicking a day PATCHes `dueDate` to the YYYY-MM-DD string the
 *     backend expects and closes the popover
 *   - "Clear" link at the bottom PATCHes to `null`
 *   - keyboard: arrow keys move the focused day across the grid,
 *     Enter commits the focused day, Esc closes (Radix handles Esc)
 *
 * The component owns its own popover state plus the `focused` day so
 * arrow-key navigation is independent of mouse focus.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useUpdateTask } from '@/lib/api/tasks';
import { cn, todayIso } from '@/lib/utils';
import type { Task } from '@workspace/shared';

interface DueDateCellProps {
  task: Task;
}

/** Trigger geometry — matches the sibling inline editors so the row
 * keeps a single line height when this cell is replaced by the popover
 * picker rather than the old `<input type="date">`. */
const TRIGGER_CLASS =
  'h-7 w-full bg-transparent px-mx-2 text-left text-mx-body-sm text-mx-grey-300 focus:outline-none focus:bg-mx-grey-900 focus:ring-1 focus:ring-mx-orange/40';

/**
 * Format the trigger label. `task.dueDate` is the wire YYYY-MM-DD
 * string; we parse via `parseISO` so the resulting Date is treated as
 * local midnight (the backend stores dueDate as a calendar date, not a
 * UTC timestamp, so the same calendar day always wins).
 */
function formatTriggerLabel(dueDate: string | null): string {
  if (!dueDate) return '—';
  return format(parseISO(dueDate), 'dd MMM yy');
}

/**
 * Build the six-row × seven-col date grid for the supplied focus
 * month. We start the grid on Monday (Australian convention; passing
 * `weekStartsOn: 1` to `date-fns`) so the columns line up with how
 * Peter reads a calendar in person. The grid always returns 42 cells
 * so the layout never reflows when navigating between months.
 */
function buildMonthGrid(focusMonth: Date): Date[] {
  const monthStart = startOfMonth(focusMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/** Convert a `Date` to the wire YYYY-MM-DD string the backend wants.
 * Using the local components (not UTC) keeps the picked day aligned
 * with what the user clicked when Brisbane is hours ahead of UTC. */
function toWireDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DueDateCell({ task }: DueDateCellProps) {
  const updateTask = useUpdateTask();
  const [open, setOpen] = useState(false);
  // Focus tracks the keyboard cursor within the grid; seeded from the
  // task's existing due date or today when the popover opens so the
  // first arrow-key press lands somewhere sensible.
  const initial = useMemo(() => {
    if (task.dueDate) return parseISO(task.dueDate);
    return parseISO(todayIso());
  }, [task.dueDate]);
  const [focusMonth, setFocusMonth] = useState<Date>(initial);
  const [focusedDay, setFocusedDay] = useState<Date>(initial);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Reseed focus when the popover opens so the grid lands on the
  // active month every time. Without this the focus would stick on
  // whichever month the user navigated to last session.
  useEffect(() => {
    if (open) {
      setFocusMonth(initial);
      setFocusedDay(initial);
    }
  }, [open, initial]);

  const days = useMemo(() => buildMonthGrid(focusMonth), [focusMonth]);
  const todayDate = useMemo(() => parseISO(todayIso()), []);
  const selectedDate = task.dueDate ? parseISO(task.dueDate) : null;

  /**
   * Commit a picked day: PATCH the dueDate field on the task and close
   * the popover. The mutation invalidates the tasks list so the table
   * re-renders with the new value.
   */
  const commitDate = useCallback(
    (d: Date) => {
      const wire = toWireDate(d);
      if (wire === task.dueDate) {
        setOpen(false);
        return;
      }
      updateTask.mutate({ id: task.id, patch: { dueDate: wire } });
      setOpen(false);
    },
    [task.dueDate, task.id, updateTask],
  );

  /** Clear the dueDate (set to null). */
  const clearDate = useCallback(() => {
    if (task.dueDate !== null) {
      updateTask.mutate({ id: task.id, patch: { dueDate: null } });
    }
    setOpen(false);
  }, [task.dueDate, task.id, updateTask]);

  /**
   * Keyboard handler scoped to the grid. Arrow keys move the focused
   * day by the relevant offset and shift the focused month when the
   * focus crosses a month boundary; Enter commits the focused day.
   * Radix Popover handles Esc.
   */
  const onGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let next: Date | null = null;
      switch (e.key) {
        case 'ArrowLeft':
          next = addDays(focusedDay, -1);
          break;
        case 'ArrowRight':
          next = addDays(focusedDay, 1);
          break;
        case 'ArrowUp':
          next = addDays(focusedDay, -7);
          break;
        case 'ArrowDown':
          next = addDays(focusedDay, 7);
          break;
        case 'Enter':
          e.preventDefault();
          commitDate(focusedDay);
          return;
        default:
          return;
      }
      e.preventDefault();
      setFocusedDay(next);
      if (!isSameMonth(next, focusMonth)) {
        setFocusMonth(startOfMonth(next));
      }
    },
    [focusedDay, focusMonth, commitDate],
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Due date for ${task.title}`}
          data-testid="due-date-trigger"
          className={TRIGGER_CLASS}
        >
          <span className="tnum">{formatTriggerLabel(task.dueDate)}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          data-testid="due-date-popover"
          className="z-50 w-[260px] rounded-none border border-mx-grey-800 bg-mx-card p-mx-3 text-mx-grey-300 shadow-none"
        >
          <header className="mb-mx-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setFocusMonth(subMonths(focusMonth, 1))}
              className="flex h-6 w-6 items-center justify-center text-mx-grey-mid hover:text-mx-white"
            >
              <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            </button>
            <h4
              data-testid="due-date-header"
              className="text-mx-body-sm font-semibold uppercase tracking-wider text-mx-grey-300"
            >
              {format(focusMonth, 'MMMM yyyy')}
            </h4>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setFocusMonth(addMonths(focusMonth, 1))}
              className="flex h-6 w-6 items-center justify-center text-mx-grey-mid hover:text-mx-white"
            >
              <ChevronRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            </button>
          </header>
          {/* Weekday header row — Monday start per Australian
              convention. Mono-label size keeps the row tight so the
              grid below fits in a 260px-wide popover. */}
          <div
            aria-hidden
            className="mono-label mb-mx-1 grid grid-cols-7 text-center text-mx-grey-mid"
          >
            {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div
            ref={gridRef}
            role="grid"
            tabIndex={0}
            onKeyDown={onGridKeyDown}
            className="grid grid-cols-7 gap-[2px] focus:outline-none"
          >
            {days.map((day) => {
              const inMonth = isSameMonth(day, focusMonth);
              const isToday = isSameDay(day, todayDate);
              const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
              const isFocused = isSameDay(day, focusedDay);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  data-testid="due-date-day"
                  data-day={toWireDate(day)}
                  aria-label={format(day, 'd MMMM yyyy')}
                  aria-selected={isSelected}
                  onClick={() => commitDate(day)}
                  className={cn(
                    'tnum h-7 text-mx-body-sm transition-colors duration-mx-default ease-mx-default',
                    inMonth ? 'text-mx-grey-300' : 'text-mx-grey-mid/60',
                    !isSelected && !isFocused && 'hover:bg-mx-grey-900',
                    isToday && !isSelected && 'border border-mx-orange/40',
                    isSelected && 'bg-mx-orange text-mx-white',
                    isFocused && !isSelected && 'ring-1 ring-mx-orange/60',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
          <footer className="mt-mx-3 flex items-center justify-between border-t border-mx-grey-800 pt-mx-2">
            <button
              type="button"
              data-testid="due-date-clear"
              onClick={clearDate}
              className="mono-label text-mx-grey-mid hover:text-mx-white"
            >
              CLEAR
            </button>
            <Popover.Close asChild>
              <button type="button" className="mono-label text-mx-grey-mid hover:text-mx-white">
                CLOSE
              </button>
            </Popover.Close>
          </footer>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
